import { useState } from 'react'
import * as adminApi from '../../adminApi'
import { ApiError } from '../../api'
import { anyAttending, formatRoomPrice } from '../../rooms'
import type { AdminInvite, RoomType } from '../../types'

interface InviteRoomProps {
  invite: AdminInvite
  types: readonly RoomType[]
  onChanged: () => void
  onSessionLost: () => void
}

/**
 * Which room in Horsley Towers this invite has, and the means to change it.
 *
 * A room belongs to the whole invite, so this sits with the code and the notes
 * rather than against any one guest.
 *
 * The console is held to the same rules as a guest: the server refuses a full
 * room and refuses an invite where nobody is coming. Rather than let that come
 * back as an error, full rooms are disabled in the list and the whole control
 * steps aside when nobody is attending - but the server is still the one saying
 * no, so a stale page cannot slip a twenty-first Delux Double through.
 */
export function InviteRoom({ invite, types, onChanged, onSessionLost }: InviteRoomProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const attending = anyAttending(invite.people)
  const currentId = invite.room?.room_type_id ?? ''

  async function run(action: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await action()
      onChanged()
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return
      }
      setError(err instanceof ApiError ? err.message : 'Could not change the room.')
    } finally {
      setBusy(false)
    }
  }

  // Nothing to offer somebody who is not coming, and the server would refuse it
  // anyway. Said rather than hidden, so a missing control is not a mystery.
  if (!attending) {
    return (
      <p className="text-ink-soft mt-3 text-xs">
        No room: nobody on this invite has said they are coming.
      </p>
    )
  }

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor={`room-${invite.id}`}
          className="text-ink-soft text-[0.65rem] tracking-widest uppercase"
        >
          Room
        </label>
        <select
          id={`room-${invite.id}`}
          value={currentId}
          disabled={busy}
          onChange={(event) => {
            const next = event.target.value
            void run(() =>
              next === '' ? adminApi.releaseRoom(invite.id) : adminApi.reserveRoom(invite.id, next),
            )
          }}
          className="border-ink/20 bg-cream rounded-md border px-2 py-1 text-sm disabled:opacity-50"
        >
          <option value="">No room</option>
          {types.map((room) => {
            const held = room.id === currentId
            const left = room.capacity - room.reserved
            return (
              <option
                key={room.id}
                value={room.id}
                // The room they are already in is never full to them - offering
                // it back is how the selection stays where it is.
                disabled={left <= 0 && !held}
              >
                {room.name}
                {held ? '' : left <= 0 ? ' - full' : ` - ${left} left`}
              </option>
            )
          })}
        </select>

        {invite.room !== null && (
          <span className="text-ink-soft text-xs">
            {formatRoomPrice(types.find((t) => t.id === currentId)?.price_pence ?? 0)}
          </span>
        )}
        {busy && <span className="text-ink-soft text-xs">Saving…</span>}
      </div>

      {/* Only once there is a room to have been paid for. Free rooms are still
          worth a tick: the venue's list has to account for them either way. */}
      {invite.room !== null && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={invite.room.paid_at !== null}
              disabled={busy}
              onChange={(event) =>
                void run(() => adminApi.setRoomPaid(invite.id, event.target.checked))
              }
              className="accent-sage size-3.5"
            />
            <span className={invite.room.paid_at !== null ? 'text-sage' : 'text-ink-soft'}>
              Paid
            </span>
          </label>
          {/* The date, because it is what reconciles against the venue's list. */}
          {invite.room.paid_at !== null && (
            <span className="text-ink-soft text-xs">{formatPaidOn(invite.room.paid_at)}</span>
          )}
        </div>
      )}

      {error !== null && (
        <p role="alert" className="text-claret mt-1 text-xs">
          {error}
        </p>
      )}
    </div>
  )
}

/** The settled date, short - the console is a list, not a receipt. */
function formatPaidOn(iso: string): string {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''
  return `on ${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
}
